<script setup lang="ts">
  import { computed } from 'vue'
  import { storeToRefs } from 'pinia'

  import { IbexaImageVariations } from '@/enums/IbexaImageVariations'
  import type { Route } from '@/types/Route'
  import type { SubjectResource } from '@/types/Subject'
  import { useImage } from '@/composables/useImage'
  import { useAuthStore } from '@/stores/auth'
  import { useAppRouting } from '@/composables/useAppRouting'

  import Typography from '@/components/common/Typography.vue'
  import RichTextRenderer from '@/components/common/RichTextRenderer.vue'

  const props = withDefaults(defineProps<{
    title: string,
    subjectResources: SubjectResource[],
    linkRoute: Route
  }>(), {
    title: 'Title',
  })

  const { getImageByField } = useImage()
  const { getRouteWithParams } = useAppRouting()
  const { isTeacher } = storeToRefs(useAuthStore())
  const isSubjectResources = computed(() => props.subjectResources.some(subjectResource => !subjectResource.isTeacherOnly || isTeacher))
</script>

<template>
  <div v-if="isSubjectResources">
    <Typography
      component="h2"
      variant="h3"
      class="subject-resources-title"
    >
      {{ props.title }}
    </Typography>
    <ul class="subject-resources">
      <template v-for="(subjectResource, index) in subjectResources">
        <li
          v-if="!subjectResource.isTeacherOnly || isTeacher"
          :key="index"
          class="resource"
          :data-testid="`subject-resource-list-item-${index}`"
        >
          <router-link
            :to="getRouteWithParams(linkRoute, subjectResource)"
            class="resource-link"
          >
            <div class="image-wrapper">
              <img
                class="resource-image"
                aria-hidden="true"
                :src="getImageByField(subjectResource.contentId, 'thumbnail', IbexaImageVariations.ThumbnailLarge)"
                :alt="subjectResource.image?.alternativeText"
              >
            </div>
            <div class="text-wrapper">
              <Typography variant="label">
                {{ subjectResource.category }}
              </Typography>
              <Typography
                component="h3"
                variant="h4"
              >
                {{ subjectResource.title }}
              </Typography>
              <RichTextRenderer
                v-if="subjectResource.description"
                :rich-text="subjectResource.description"
              />
            </div>
          </router-link>
        </li>
      </template>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
  $resource-padding: 28px;
  $edge-cell-size: calc(25% - $resource-padding / 2);
  $middle-cell-size: calc(25% + $resource-padding / 2 + .0625rem);
  $tablet-edge-cell-size: calc(calc(100% / 3) - 1 / 3 * $resource-padding);
  $tablet-middle-cell-size: calc(calc(100% / 3) + 2 / 3 * $resource-padding);
  $red-border: .0625rem solid var(--color-red-10);
  $single-column-breakpoint: 540px;

  .subject-resources-title {
    margin: 0 0 18px;
  }

  .subject-resources {
    overflow: hidden;
    list-style: none;
    display: grid;
    grid-template-columns: $edge-cell-size $middle-cell-size $middle-cell-size $edge-cell-size;
    margin: 0;
    padding: 4px 0 0;
    border-top: $red-border;
    border-bottom: $red-border;

    @include vgs-media-tablet {
      grid-template-columns: $tablet-edge-cell-size $tablet-middle-cell-size $tablet-edge-cell-size;
    }

    @include vgs-media-mobile {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (width <= $single-column-breakpoint) {
      grid-template-columns: 1fr;
    }
  }

  .resource {
    padding: $resource-padding;
    width: 100%;
    border-bottom: $red-border;
    margin-bottom: -.0625rem;
    position: relative;

    // Desktop only
    @include vgs-media-desktop {
      &:nth-child(4n + 1) {
        padding-left: 0;

        &::before {
          content: unset;
        }
      }

      &:nth-child(4n) {
        padding-right: 0;
      }
    }

    // Tablet only
    @include vgs-media-tablet {
      &:nth-child(3n + 1) {
        padding-left: 0;

        &::before {
          content: unset;
        }
      }

      &:nth-child(3n) {
        padding-right: 0;
      }
    }

    // Mobile only
    @include vgs-media-mobile {
      &:nth-child(2n + 1) {
        padding-left: 0;

        &::before {
          content: unset;
        }
      }

      &:nth-child(2n) {
        padding-right: 0;
      }
    }

    @media (width <= $single-column-breakpoint) {
      padding-left: 0;
      padding-right: 0;
    }

    &::before,
    &:last-child::after {
      position: absolute;
      top: 50%;
      left: 0;
      content: '';
      background: var(--color-red-10);
      transform: translate(-.0625rem, -50%);
      width: .0625rem;
      height: calc(100% - $resource-padding * 2);
    }

    &:last-child::after {
      left: calc(100% + .0625rem);
    }
  }

  .resource-link {
    display: block;
    text-decoration: none;
    color: inherit;

    &:hover {
      cursor: pointer;
    }

    &:focus-visible {
      outline: 3px solid var(--ks-focusring);
      outline-offset: 4px;
    }
  }

  .image-wrapper {
    position: relative;
    transition: all .15s cubic-bezier(.22, 0, .69, 1.15);

    &:hover {
      filter: brightness(.9);
      translate: 0 -3px;
    }
  }

  .resource-image {
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    overflow: hidden;
    margin-bottom: 16px;
  }

  .text-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;

    * {
      margin: 0;
    }

    .cms-p {
      font-size: 1rem;
    }
  }
</style>
